<template>
  <q-dialog v-model="isOpen" persistent>
    <q-card style="min-width: 800px; max-width: 90vw;">
      <!-- Header -->
      <q-card-section class="bg-primary text-white">
        <div class="text-h6">Manage SAX Component Kits</div>
        <div class="text-caption">{{ equipmentName }}</div>
      </q-card-section>

      <!-- Loading State -->
      <q-inner-loading :showing="loading">
        <q-spinner-gears size="50px" color="primary" />
      </q-inner-loading>

      <!-- Content -->
      <q-card-section v-if="!loading" style="max-height: 70vh;" class="scroll">
        <div class="row q-col-gutter-md">
          <!-- Selected Kits (Left Column) -->
          <div class="col-6">
            <div class="text-h6 q-mb-md">
              Selected Kits
              <q-btn
                flat
                dense
                round
                icon="refresh"
                size="sm"
                color="primary"
                class="q-ml-sm"
                @click="loadKits"
              >
                <q-tooltip>Refresh</q-tooltip>
              </q-btn>
            </div>

            <!-- Auto-select button -->
            <q-btn
              v-if="selectedKits.length === 0"
              outline
              color="primary"
              icon="auto_fix_high"
              label="Auto-Select Recommended Kits"
              class="q-mb-md full-width"
              :loading="autoSelecting"
              @click="autoSelectKits"
            />

            <!-- Selected kits list -->
            <q-list v-if="selectedKits.length > 0" bordered separator>
              <q-item
                v-for="kit in selectedKits"
                :key="`${kit.kit_name}-${kit.kit_version}`"
                clickable
                :active="selectedKit?.id === kit.id"
                @click="selectKit(kit)"
              >
                <q-item-section>
                  <q-item-label>
                    {{ kit.kit_name }}
                    <q-badge v-if="kit.is_basic_kit" color="green" class="q-ml-xs">
                      Basic
                    </q-badge>
                  </q-item-label>
                  <q-item-label caption>
                    Version: {{ kit.kit_version }}
                    <span v-if="kit.component_count">
                      · {{ kit.component_count }} components
                    </span>
                  </q-item-label>
                  <q-item-label v-if="kit.is_aggregated" caption class="text-orange">
                    Aggregated from {{ kit.aggregated_from?.length || 0 }} equipment
                  </q-item-label>
                </q-item-section>

                <q-item-section side>
                  <q-btn
                    v-if="!kit.is_basic_kit && !kit.is_aggregated"
                    flat
                    dense
                    round
                    icon="delete"
                    color="negative"
                    size="sm"
                    @click.stop="removeKit(kit)"
                  >
                    <q-tooltip>Remove Kit</q-tooltip>
                  </q-btn>
                  <q-tooltip v-else-if="kit.is_basic_kit">
                    Basic kits cannot be removed
                  </q-tooltip>
                  <q-tooltip v-else-if="kit.is_aggregated">
                    Aggregated kits are managed automatically
                  </q-tooltip>
                </q-item-section>
              </q-item>
            </q-list>

            <div v-else class="text-center q-pa-md text-grey-6">
              <q-icon name="info" size="md" class="q-mb-sm" />
              <div>No kits selected</div>
              <div class="text-caption">Use auto-select to add recommended kits</div>
            </div>
          </div>

          <!-- Available Kits / Kit Details (Right Column) -->
          <div class="col-6">
            <!-- Kit details view when kit is selected -->
            <div v-if="selectedKit">
              <div class="text-h6 q-mb-md">Kit Details</div>

              <q-card flat bordered>
                <q-card-section>
                  <div class="text-subtitle1">{{ selectedKit.kit_name }}</div>
                  <div class="text-caption text-grey-7">
                    Current Version: {{ selectedKit.kit_version }}
                  </div>
                </q-card-section>

                <q-separator />

                <!-- Version upgrade section -->
                <q-card-section v-if="availableVersions.length > 1">
                  <div class="text-subtitle2 q-mb-sm">Available Versions</div>

                  <q-select
                    v-model="newVersion"
                    :options="versionOptions"
                    label="Select Version"
                    outlined
                    dense
                    emit-value
                    map-options
                  />

                  <q-btn
                    v-if="newVersion && newVersion !== selectedKit.kit_version"
                    color="primary"
                    label="Change Version"
                    icon="upgrade"
                    class="q-mt-sm full-width"
                    :loading="changingVersion"
                    @click="changeVersion"
                  />

                  <!-- Validation warnings -->
                  <div v-if="validationIssues.length > 0" class="q-mt-md">
                    <q-banner
                      v-for="(issue, index) in validationIssues"
                      :key="index"
                      :class="`q-mb-sm bg-${issue.severity === 'high' ? 'negative' : 'warning'}`"
                      class="text-white"
                    >
                      <template #avatar>
                        <q-icon :name="issue.severity === 'high' ? 'error' : 'warning'" />
                      </template>
                      {{ issue.message }}
                    </q-banner>
                  </div>
                </q-card-section>

                <q-card-section v-else>
                  <div class="text-caption text-grey-7">
                    This is the only version available for this kit.
                  </div>
                </q-card-section>
              </q-card>
            </div>

            <!-- Add new kit view -->
            <div v-else>
              <div class="text-h6 q-mb-md">Add Kit</div>

              <q-select
                v-model="newKitSelection"
                :options="availableKitsOptions"
                label="Select Kit to Add"
                outlined
                dense
                use-input
                input-debounce="300"
                option-label="label"
                option-value="value"
                emit-value
                map-options
                @filter="filterAvailableKits"
              >
                <template #no-option>
                  <q-item>
                    <q-item-section class="text-grey">
                      No kits available
                    </q-item-section>
                  </q-item>
                </template>
              </q-select>

              <q-btn
                v-if="newKitSelection"
                color="primary"
                label="Add Kit"
                icon="add"
                class="q-mt-sm full-width"
                :loading="adding"
                @click="addKit"
              />
            </div>
          </div>
        </div>
      </q-card-section>

      <!-- Actions -->
      <q-card-actions align="right">
        <q-btn flat label="Close" color="primary" @click="close" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup>
  import { ref, computed, watch } from 'vue';
  import { api } from '@/services/api';
  import { useQuasar } from 'quasar';

  const props = defineProps({
    modelValue: {
      type: Boolean,
      required: true,
    },
    equipmentId: {
      type: Number,
      required: true,
    },
    equipmentName: {
      type: String,
      default: 'Equipment',
    },
  });

  const emit = defineEmits(['update:modelValue', 'kits-updated']);

  const $q = useQuasar();

  const isOpen = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
  });

  const loading = ref(false);
  const autoSelecting = ref(false);
  const adding = ref(false);
  const changingVersion = ref(false);

  const selectedKits = ref([]);
  const selectedKit = ref(null);
  const availableKits = ref([]);
  const availableVersions = ref([]);
  const newVersion = ref(null);
  const newKitSelection = ref(null);
  const validationIssues = ref([]);

  const availableKitsOptions = ref([]);

  // Computed
  const versionOptions = computed(() => {
    return availableVersions.value.map((v) => ({
      label: v,
      value: v,
    }));
  });

  // Watch for dialog open
  watch(() => props.modelValue, (newVal) => {
    if (newVal) {
      loadKits();
      loadAvailableKits();
    }
  });

  // Methods
  async function loadKits() {
    try {
      loading.value = true;
      const response = await api.get(`/equipment/${props.equipmentId}/kits`);
      selectedKits.value = response || [];
    } catch (error) {
      console.error('[Kit Manager] Error loading kits:', error);
      $q.notify({
        type: 'negative',
        message: 'Failed to load kits',
        caption: error.response?.data?.message || error.message,
      });
    } finally {
      loading.value = false;
    }
  }

  async function loadAvailableKits() {
    try {
      const response = await api.get('/v2/sax/kits');
      availableKits.value = response || [];

      // Build options for select
      availableKitsOptions.value = availableKits.value
        .filter((kit) => !selectedKits.value.some((sk) => sk.kit_name === kit.name))
        .map((kit) => ({
          label: `${kit.name} (${kit.versions.length} version${kit.versions.length > 1 ? 's' : ''})`,
          value: kit.name,
        }));

    } catch (error) {
      console.error('[Kit Manager] Error loading available kits:', error);
    }
  }

  function filterAvailableKits(val, update) {
    update(() => {
      const needle = val.toLowerCase();
      availableKitsOptions.value = availableKits.value
        .filter((kit) => !selectedKits.value.some((sk) => sk.kit_name === kit.name))
        .filter((kit) => kit.name.toLowerCase().includes(needle))
        .map((kit) => ({
          label: `${kit.name} (${kit.versions.length} version${kit.versions.length > 1 ? 's' : ''})`,
          value: kit.name,
        }));
    });
  }

  async function autoSelectKits() {
    try {
      autoSelecting.value = true;
      const response = await api.post(`/equipment/${props.equipmentId}/kits/auto-select`);

      $q.notify({
        type: 'positive',
        message: 'Kits auto-selected successfully',
        caption: `Added ${response?.length || 0} kits`,
      });

      await loadKits();
      emit('kits-updated');
    } catch (error) {
      console.error('[Kit Manager] Error auto-selecting kits:', error);
      $q.notify({
        type: 'negative',
        message: 'Failed to auto-select kits',
        caption: error.response?.data?.message || error.message,
      });
    } finally {
      autoSelecting.value = false;
    }
  }

  async function addKit() {
    if (!newKitSelection.value) {return;}

    try {
      adding.value = true;

      // Find kit and get latest version
      const kit = availableKits.value.find((k) => k.name === newKitSelection.value);
      if (!kit || !kit.versions || kit.versions.length === 0) {
        throw new Error('Kit not found or has no versions');
      }

      const latestVersion = kit.versions[0].version; // Versions are ordered desc, get version string

      const response = await api.post(`/equipment/${props.equipmentId}/kits`, {
        kit_name: newKitSelection.value,
        kit_version: latestVersion,
      });

      $q.notify({
        type: 'positive',
        message: response.message || 'Kit added successfully',
      });

      newKitSelection.value = null;
      await loadKits();
      await loadAvailableKits();
      emit('kits-updated');
    } catch (error) {
      console.error('[Kit Manager] Error adding kit:', error);
      $q.notify({
        type: 'negative',
        message: 'Failed to add kit',
        caption: error.response?.data?.message || error.message,
      });
    } finally {
      adding.value = false;
    }
  }

  async function removeKit(kit) {
    if (kit.is_basic_kit) {
      $q.notify({
        type: 'warning',
        message: 'Basic kits cannot be removed',
      });
      return;
    }

    if (kit.is_aggregated) {
      $q.notify({
        type: 'warning',
        message: 'Aggregated kits are managed automatically',
      });
      return;
    }

    try {
      await api.delete(`/equipment/${props.equipmentId}/kits/${kit.id}`);

      $q.notify({
        type: 'positive',
        message: 'Kit removed successfully',
      });

      await loadKits();
      await loadAvailableKits();
      selectedKit.value = null;
      emit('kits-updated');
    } catch (error) {
      console.error('[Kit Manager] Error removing kit:', error);
      $q.notify({
        type: 'negative',
        message: 'Failed to remove kit',
        caption: error.response?.data?.message || error.message,
      });
    }
  }

  async function selectKit(kit) {
    selectedKit.value = kit;
    newVersion.value = kit.kit_version;
    validationIssues.value = [];

    // Load available versions for this kit
    try {
      const response = await api.get(`/equipment/${props.equipmentId}/kits/${kit.id}/versions`);
      availableVersions.value = response || [];
    } catch (error) {
      console.error('[Kit Manager] Error loading versions:', error);
      availableVersions.value = [kit.kit_version];
    }
  }

  async function changeVersion() {
    if (!selectedKit.value || !newVersion.value) {return;}
    if (newVersion.value === selectedKit.value.kit_version) {return;}

    try {
      changingVersion.value = true;

      const response = await api.put(
        `/equipment/${props.equipmentId}/kits/${selectedKit.value.id}/version`,
        { new_version: newVersion.value },
      );

      if (response.warnings && response.warnings.length > 0) {
        validationIssues.value = response.warnings;
        $q.notify({
          type: 'warning',
          message: 'Version changed with warnings',
          caption: 'Review the warnings below',
        });
      } else {
        $q.notify({
          type: 'positive',
          message: response.message || 'Version changed successfully',
        });
      }

      await loadKits();
      selectedKit.value = null;
      emit('kits-updated');
    } catch (error) {
      console.error('[Kit Manager] Error changing version:', error);

      if (error.response?.data?.issues) {
        validationIssues.value = error.response.data.issues;
      }

      $q.notify({
        type: 'negative',
        message: 'Failed to change version',
        caption: error.response?.data?.message || error.message,
      });
    } finally {
      changingVersion.value = false;
    }
  }

  function close() {
    isOpen.value = false;
    selectedKit.value = null;
    newKitSelection.value = null;
    validationIssues.value = [];
  }
</script>

<style scoped>
.q-item.q-item--active {
  background-color: rgba(25, 118, 210, 0.1);
}
</style>
